import type { Deal } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { ChannelPartnerSection } from "@/components/deals/ChannelPartnerSection";
import { DealConflictIndicator } from "@/components/deals/DealConflictIndicator";
import { DealDecisionActions } from "@/components/deals/DealDecisionActions";
import { DealInternalNotesSection } from "@/components/deals/DealInternalNotesSection";
import { DealProfileSections } from "@/components/deals/DealProfileSections";
import { DealStatusSection } from "@/components/deals/DealStatusSection";
import { DealWinVerifyActions } from "@/components/deals/DealWinVerifyActions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface Props {
  deal: Deal;
  /** Channel partner (reseller) name — for the decision confirm/toast copy. */
  partnerName?: string | null;
  /** Forwarded to the decision actions — runs after a decision's toast + hold. */
  onDone: () => void;
}

export function DealAdjudicationWorkspace({ deal, partnerName, onDone }: Props) {
  const { accessToken } = useCurrentUser();

  return (
    <div className="space-y-6">
      {/* CHP-415: the closed-won review leads with the Status (pill shape, via
          the shared DealStatusSection) ABOVE "The Deal", matching every other
          detail page. The old bespoke Status card duplicated the "Status" label
          (section title + inner field) and used plain text — both removed. */}
      {deal.deal_status === "win_pending_review" ? (
        <Card>
          <CardContent className="pt-6">
            <DealStatusSection deal={deal} viewer="manager" />
          </CardContent>
        </Card>
      ) : null}

      {/* Closed-won review (CHP-236): the Verify PO details form. CHP-775 moves
          it directly under the Status summary and ABOVE "The Deal" so a reviewer
          confirms the PO details first, then reads the deal record below. */}
      {deal.deal_status === "win_pending_review" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Verify PO details</CardTitle>
          </CardHeader>
          <CardContent>
            <DealWinVerifyActions deal={deal} onDone={onDone} />
          </CardContent>
        </Card>
      )}

      {/* ABN Check — a registration-review affordance (CHP-223/CHP-256). The
          indicator renders its own "ABN Check" heading + binary pill. Surfaced
          above "The Deal" so reviewers see conflict status first (CHP-393). */}
      {accessToken && deal.deal_status === "pending_review" ? (
        <Card>
          <CardContent className="pt-6">
            <DealConflictIndicator deal={deal} accessToken={accessToken} />
          </CardContent>
        </Card>
      ) : null}

      {/* The Deal — profile, restructured into Client / Opportunity / Proposed
          Solution per CHP-223. The SLA banner + title/subtitle are page chrome
          (rendered by the route). */}
      <Card>
        <CardHeader>
          <CardTitle>The Deal</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* CHP-378: owner org + lead + contact (phone first, email second),
              mirroring the detail view's ManagerDealDetail composition. */}
          <ChannelPartnerSection deal={deal} />
          <DealProfileSections deal={deal} />
        </CardContent>
      </Card>

      {/* Review Outcome — registration approval (CHP-223). */}
      {deal.deal_status === "pending_review" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Review Outcome</CardTitle>
          </CardHeader>
          <CardContent>
            <DealDecisionActions deal={deal} partnerName={partnerName} onDone={onDone} />
          </CardContent>
        </Card>
      )}

      {/* CHP-413: surface prior internal notes on BOTH decision surfaces so a
          CM/RA/RM sees the running ops record (registration / extension /
          closed-won notes) while deciding. The section is manager-gated — it
          returns null for a non-manager — so it never leaks to a partner. */}
      {(deal.deal_status === "pending_review" || deal.deal_status === "win_pending_review") && (
        <Card>
          <CardContent className="pt-6">
            <DealInternalNotesSection deal={deal} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
